import store from '@/store'
import customMenu from '@/config/customMenu'
import { logError } from '@/utils/logger'
import { HTTPError } from '@/utils/http'

export default [
  {
    path: '/',
    redirect: {
      name: 'jobs-list'
    }
  },
  {
    path: '/jobs',
    name: 'jobs-list',
    component: () => import('../pages/Jobs'),
    async beforeEnter (routeTo) {
      await store.dispatch('jobGroups/fetchGroups')
    },
    meta: {
      authRequired: true,
      allowCustomerSwitch: true
    }
  },
  {
    path: '/job/:id',
    name: 'job-view',
    component: () => import('../pages/JobConfiguration'),
    props: true,
    async beforeEnter (routeTo) {
      store.commit('job/SET_IS_READ_ONLY', true)

      await Promise.all([
        store.dispatch('job/fetchJob', { id: routeTo.params.id }),
        store.dispatch('dataconnectors/fetchDataconnectors')
      ])
    },
    meta: {
      authRequired: true
    }
  },
  {
    path: '/job/:id/config',
    name: 'job-config',
    component: () => import('../pages/JobConfiguration'),
    props: true,
    async beforeEnter (routeTo) {
      store.commit('job/SET_IS_READ_ONLY', false)

      await Promise.all([
        store.dispatch('job/fetchJob', { id: routeTo.params.id }),
        store.dispatch('dataconnectors/fetchDataconnectors')
      ])
    },
    meta: {
      authRequired: true
    }
  },
  // TODO: job-create route
  {
    path: '/job-config/:id',
    redirect: '/job/:id/config'
  },
  {
    path: '/job/:id/records',
    name: 'job-records',
    component: () => import('../pages/JobRecords'),
    async beforeEnter (routeTo) {
      store.commit('jobRecords/RESET')

      await store.dispatch('job/fetchJob', { id: routeTo.params.id })
    },
    meta: {
      authRequired: true
    }
  },
  {
    path: '/job-records/:id',
    redirect: '/job/:id?/records'
  },
  {
    path: '/job-groups',
    name: 'job-groups',
    component: () => import('../pages/JobGroups'),
    async beforeEnter (routeTo) {
      await store.dispatch('jobGroups/fetchGroups', true)
    },
    meta: {
      authRequired: true,
      allowCustomerSwitch: true
    }
  },
  {
    path: '/job/:id/events',
    name: 'job-events',
    component: () => import('../pages/JobEvents'),
    meta: {
      authRequired: true
    },
    async beforeEnter (routeTo, routeFrom, next) {
      await Promise.all([
        store.dispatch('job/fetchJob', { id: routeTo.params.id }),
        store.dispatch('jobEvents/fetchEvents', { jobId: routeTo.params.id })
      ])
    }
  },
  {
    path: '/customers',
    name: 'customers',
    component: () => import('../pages/CustomerOverview'),
    meta: {
      authRequired: true
    },
    async beforeEnter (routeTo, routeFrom, next) {
      store.dispatch('jobs/stopTimer')

      // just in case no customers are in store
      if (!store.getters['customers/list'].length) {
        await store.dispatch('customers/fetchCustomers')
      }

      if (store.getters['customers/customerGuid']) {
        store.dispatch('jobs/setTimer', true)
        next('/')
      } else {
        next()
      }
    }
  },
  {
    path: '/record-statistics',
    name: 'record-statistics',
    component: () => import('../pages/RecordStatistics'),
    meta: {
      authRequired: true,
      allowCustomerSwitch: true
    },
    async beforeEnter (routeTo) {
      await store.dispatch('recordStatistics/fetchMonthlyRecords', routeTo.query)
    }
  },
  {
    path: '/feedback',
    name: 'feedback',
    component: () => import('../pages/Feedback'),
    meta: {
      authRequired: true,
      allowCustomerSwitch: true
    },
    async beforeEnter (routeTo, routeFrom, next) {
      await store.dispatch('feedbackItems/fetchFeedbackItems', routeTo.query)
    }
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../pages/Login'),
    beforeEnter (routeTo, routeFrom, next) {
      store.dispatch('reset')
      next()
    }
  },
  {
    path: '/login/sso',
    name: 'login-sso',
    component: () => import('../pages/LoginSso'),
    beforeEnter (routeTo, routeFrom, next) {
      store.dispatch('reset')
      next()
    }
  },
  {
    path: '/password-reset',
    name: 'password-reset',
    component: () => import('../pages/PasswordReset'),
    beforeEnter (routeTo, routeFrom, next) {
      next()
    },
    meta: {
      authRequired: true
    }
  },
  {
    path: '/login/loading',
    name: 'login-loading',
    component: () => import('../pages/LoginLoading'),
    beforeEnter (routeTo, routeFrom, next) {
      next()
    }
  },
  {
    path: '/custom/:name',
    name: 'custom',
    component: () => import('../pages/Custom'),
    beforeEnter (routeTo, routeFrom, next) {
      const customerPages = customMenu[store.getters['customers/customer'].ID] || []
      const customPage = customerPages.find(item => item.name === routeTo.params.name)
      const requiredPrivilege = customPage.privilege

      if (customPage && store.getters['auth/privileges'].includes(requiredPrivilege)) {
        next()
      } else {
        next('/404')
      }
    },
    meta: {
      authRequired: true
    }
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      authRequired: true
    },
    beforeEnter (routeTo, routeFrom, next) {
      store.dispatch('reset')

      next('login')
    }
  },
  {
    path: '/404',
    name: '404',
    component: () => import('../pages/_404'),
    props: true,
    async beforeEnter (routeTo, routeFrom) {
      if (!routeFrom.name && store.getters['auth/loggedIn']) {
        await Promise.all([
          store.dispatch('auth/fetchPrivileges'),
          store.dispatch('customers/fetchCustomers')
        ])
      }
    }
  },
  {
    path: '/500',
    name: '500',
    component: () => import('../pages/_500'),
    props: true,
    async beforeEnter (routeTo, routeFrom) {
      if (!routeFrom.name && store.getters['auth/loggedIn']) {
        await Promise.all([
          store.dispatch('auth/fetchPrivileges'),
          store.dispatch('customers/fetchCustomers')
        ])
      }
    }
  },
  {
    path: '/:pathMatch(.*)',
    beforeEnter (routeTo, routeFrom, next) {
      logError(new HTTPError('Page not found'), { routeTo, routeFrom })
      next({ name: '404' })
    }
  }
]
